import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    ViewChild,
    AfterViewInit,
    OnDestroy,
} from '@angular/core'
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { viewerActions } from 'src/app/state/viewer/viewer.actions'
import { isManagedLayer } from 'src/app/state/viewer/viewer-layer-utils'
import {
    selectInvisibleLayers,
    selectLayerInfosForGroupId,
} from 'src/app/state/viewer/viewer.selectors'

const DISPLAY_LIMIT = 100

type LoadStatusFilter = 'all' | 'loaded' | 'unloaded'

export interface FavoriteLayersDialogData {
    groupId?: string
    /** Initial load-status tab; defaults to "all". */
    initialLoadStatusFilter?: LoadStatusFilter
}

interface LayerListItem {
    id: string
    name: string
    loaded: boolean
}

const buildDisplayedLayers = (
    matchingLayers: LayerListItem[],
    limit: number,
    loadStatusFilter: LoadStatusFilter
): { displayed: LayerListItem[]; hiddenCount: number } => {
    const sorted = [...matchingLayers].sort((a, b) => a.name.localeCompare(b.name))

    if (loadStatusFilter === 'loaded') {
        return { displayed: sorted.filter(layer => layer.loaded), hiddenCount: 0 }
    }

    if (loadStatusFilter === 'unloaded') {
        const displayed = sorted.slice(0, limit)
        return { displayed, hiddenCount: sorted.length - displayed.length }
    }

    const loaded = sorted.filter(layer => layer.loaded)
    const unloaded = sorted.filter(layer => !layer.loaded)
    const unloadedLimit = Math.max(0, limit - loaded.length)
    const displayedUnloaded = unloaded.slice(0, unloadedLimit)
    const displayed = [...loaded, ...displayedUnloaded].sort((a, b) => a.name.localeCompare(b.name))
    return { displayed, hiddenCount: unloaded.length - displayedUnloaded.length }
}

@Component({
    selector: 'app-favorite-layers-dialog',
    templateUrl: './favorite-layers-dialog.component.html',
    styleUrls: ['./favorite-layers-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoriteLayersDialogComponent implements AfterViewInit, OnDestroy {
    @ViewChild('filterInput') filterInput?: ElementRef<HTMLInputElement>

    private readonly filterText$ = new BehaviorSubject('')
    private readonly loadStatusFilter$ = new BehaviorSubject<LoadStatusFilter>(
        this.data?.initialLoadStatusFilter ?? 'all'
    )
    private readonly destroy$ = new Subject<void>()

    readonly favoriteLayers$: Observable<LayerListItem[]> = combineLatest({
        unfavoritedLayers: this.store.select(selectInvisibleLayers),
        layerInfos: this.store.select(selectLayerInfosForGroupId(this.data?.groupId!)),
    }).pipe(
        map(({ unfavoritedLayers, layerInfos }) => {
            const groupId = this.data?.groupId
            const loadedLayers: LayerListItem[] = layerInfos.map(info => ({
                id: info.id,
                name: info.name,
                loaded: true,
            }))
            const invisibleLayers: LayerListItem[] = unfavoritedLayers
                .filter(l => l.groupId === groupId && l.id && l.name && isManagedLayer(l))
                .map(l => ({
                    id: l.id!,
                    name: l.name!,
                    loaded: false,
                }))
            return [...loadedLayers, ...invisibleLayers].sort((a, b) =>
                a.name.localeCompare(b.name)
            )
        })
    )

    readonly filteredLayers$ = combineLatest({
        layers: this.favoriteLayers$,
        filter: this.filterText$,
    }).pipe(
        map(({ layers, filter }) => {
            const trimmed = filter.trim().toLowerCase()
            if (!trimmed) return layers
            return layers.filter(l => l.name.toLowerCase().includes(trimmed))
        })
    )

    readonly viewLayers$ = combineLatest({
        layers: this.filteredLayers$,
        loadStatusFilter: this.loadStatusFilter$,
    }).pipe(
        map(({ layers, loadStatusFilter }) => {
            if (loadStatusFilter === 'loaded') {
                return layers.filter(layer => layer.loaded)
            }
            if (loadStatusFilter === 'unloaded') {
                return layers.filter(layer => !layer.loaded)
            }
            return layers
        })
    )

    allLayersCount = 0
    /** Layers matching the name filter (ignoring load-status filter). */
    private textMatchingLayers: LayerListItem[] = []
    displayedLayers: LayerListItem[] = []
    hiddenLayerCount = 0
    loadedLayersCount = 0
    unloadedLayersCount = 0
    loadStatusFilter: LoadStatusFilter = 'all'
    private layersChanged = false

    set filterText(value: string) {
        this.filterText$.next(value)
    }

    get filterText(): string {
        return this.filterText$.value
    }

    constructor(
        private readonly dialogRef: DialogRef<boolean | undefined>,
        @Inject(DIALOG_DATA) readonly data: FavoriteLayersDialogData,
        private readonly store: Store,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.favoriteLayers$.pipe(takeUntil(this.destroy$)).subscribe(layers => {
            this.allLayersCount = layers.length
            this.loadedLayersCount = layers.filter(l => l.loaded).length
            this.unloadedLayersCount = layers.length - this.loadedLayersCount
            this.cdr.markForCheck()
        })

        this.filteredLayers$.pipe(takeUntil(this.destroy$)).subscribe(layers => {
            this.textMatchingLayers = layers
            this.cdr.markForCheck()
        })

        combineLatest({
            layers: this.viewLayers$,
            loadStatusFilter: this.loadStatusFilter$,
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ layers, loadStatusFilter }) => {
                this.loadStatusFilter = loadStatusFilter
                const { displayed, hiddenCount } = buildDisplayedLayers(
                    layers,
                    DISPLAY_LIMIT,
                    loadStatusFilter
                )
                this.displayedLayers = displayed
                this.hiddenLayerCount = hiddenCount
                this.cdr.markForCheck()
            })
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => this.filterInput?.nativeElement.focus())
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    setLoadStatusFilter(filter: LoadStatusFilter): void {
        this.loadStatusFilter$.next(filter)
    }

    trackByLayerId(_index: number, layer: LayerListItem): string {
        return layer.id
    }

    addLayer(layer: LayerListItem): void {
        if (layer.loaded) return
        this.layersChanged = true
        this.store.dispatch(
            viewerActions.setLocalLayerVisibility({
                id: layer.id,
                visible: true,
            })
        )
    }

    removeLayer(layer: LayerListItem): void {
        if (!layer.loaded) return
        this.layersChanged = true
        this.store.dispatch(
            viewerActions.setLocalLayerVisibility({
                id: layer.id,
                visible: false,
            })
        )
    }

    deselectAllLoaded(): void {
        const trimmed = this.filterText.trim().toLowerCase()
        const layersToUnload = this.textMatchingLayers.filter(layer => {
            if (!layer.loaded) return false
            if (!trimmed) return true
            return layer.name.toLowerCase().includes(trimmed)
        })
        layersToUnload.forEach(layer => this.removeLayer(layer))
    }

    close(): void {
        this.dialogRef.close(this.layersChanged)
    }
}
